import React, { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RouteConstants from "../../../constants/routeConstants.json";
import {
  addTenantApi,
  subscriptionPlanListApi,
  commonPlanList,
} from "../../../api/Api";
import {
  validateName,
  validateEmail,
  validateMobileNumber,
  validateNameWithNum,
} from "../../../utils/service";
import { useCookies } from "react-cookie";

export const AddTenantContext = createContext({});

export const AddTenantContextProvider = ({ children }) => {
  const [spinnerStatus, setSpinnerStatus] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState();
  const [organizationName, setOrganizationName] = useState("");
  const [organizationEmail, setOrganizationEmail] = useState("");
  const [organizationType, setOrganizationType] = useState("");
  const [location, setLocation] = useState("");
  const [subscriptionPlansList, setSubscriptionPlansList] = useState([]);

  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  // const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState("");

  const [validationErrors, setValidationErrors] = useState({});
  const [submitSuccessPopup, setSubmitSuccessPopup] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState(false);
  const [addErrorPopup, setAddErrorPopup] = useState(false);

  const [errorPopup, setErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState(false);
  const [statusValue, setStatusValue] = useState();
  const [buttonText, setButtonText] = useState("");
  const [cookies, setCookie, removeCookie] = useCookies(["user"]);

  const navigate = useNavigate();

  useEffect(() => {
    getSubscriptionPlans();
  }, []);

  const getSubscriptionPlans = async () => {
    try {
      setSpinnerStatus(true);
      const listApiData = await commonPlanList();
      // console.log("subscriptionPlans", listApiData.data.data.subscriptionPlans);
      setSubscriptionPlansList(listApiData.data.data.subscriptionPlans);
    } catch (error) {
      const responseStatus = error?.response?.data?.statusCode;
      const errorMessage =
        error?.response?.data?.message || "Something went wrong...";

      setStatusValue(responseStatus);
      setErrorPopupMessage(errorMessage);
      setErrorPopup(true);

      if (responseStatus === 400) {
        setButtonText("Try Again");
      } else if (responseStatus === 401 || responseStatus === 403) {
        setButtonText("Login");
      } else {
        setButtonText("Try Again");
      }
    }
    setSpinnerStatus(false);
  };

  const validateTenantDetails = (details) => ({
    name: !details.name.trim()
      ? "Please enter a name"
      : validateName(details.name)
      ? ""
      : "Invalid name",
    email: !details.email.trim()
      ? "Please enter an email"
      : validateEmail(details.email)
      ? ""
      : "Invalid email format",
    phone: !details.phone.trim()
      ? "Please enter a phone number"
      : validateMobileNumber(details.phone)
      ? ""
      : "Invalid phone number. Must be 10 digits",
    organizationName: !details.organizationName.trim()
      ? "Please enter an organization name"
      : validateNameWithNum(details.organizationName)
      ? ""
      : "Invalid organization name",
    organizationEmail: !details.organizationEmail.trim()
      ? "Please enter an email"
      : validateEmail(details.organizationEmail)
      ? ""
      : "Invalid email format",
    organizationType: !details.organizationType.trim()
      ? "Please enter an organization type"
      : "",
    location: !details.location.trim() ? "Please enter a location" : "",
    subscriptionPlan: !details.subscriptionPlan.trim()
      ? "Please select a subscription plan"
      : "",
  });

  // Helper function: Handle API errors
  const handleApiError = (error) => {
    const errorMessage =
      error?.response?.data?.message || "An unexpected error occurred.";
    const responseStatus = error?.response?.data?.statusCode;

    setAddErrorPopup(true);
    setErrorAlertMessage(errorMessage);
    setStatusValue(responseStatus);
    setErrorPopupMessage(errorMessage);

    const buttonText =
      responseStatus === 400
        ? "Try Again"
        : responseStatus === 401 || responseStatus === 403
        ? "Login"
        : "Try Again";

    setButtonText(buttonText);
  };

  const createTenant = async (event) => {
    event.preventDefault();

    const tenantDetails = {
      name: (name ?? "").trim(),
      email: (email ?? "").trim(),
      phone: (phone ?? "").trim(),
      organizationName: (organizationName ?? "").trim(),
      organizationEmail: (organizationEmail ?? "").trim(),
      organizationType: (organizationType ?? "").trim(),
      location: (location ?? "").trim(),
      subscriptionPlan: (subscriptionPlan ?? "").trim(),
    };

    const errors = validateTenantDetails(tenantDetails);
    setValidationErrors(errors);

    const hasErrors = Object.values(errors).some((error) => error !== "");
    if (hasErrors) return;

    try {
      const addData = await addTenantApi(tenantDetails);
      if (addData?.status === 200) {
        setSubmitSuccessPopup(true);
        setErrorAlertMessage(addData?.data?.message);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleBlur = (field) => {
    const fieldErrors = {};

    if (field === "name") {
      if (!name.trim()) {
        fieldErrors.name = "Please enter a name";
      } else if (!validateName(name)) {
        fieldErrors.name = "Invalid name";
      }
    }

    if (field === "email") {
      if (!email.trim()) {
        fieldErrors.email = "Please enter an email";
      } else if (!validateEmail(email)) {
        fieldErrors.email = "Invalid email format";
      }
    }

    if (field === "phone") {
      if (!phone.trim()) {
        fieldErrors.phone = "Please enter a phone number";
      } else if (!validateMobileNumber(phone)) {
        fieldErrors.phone = "Invalid phone number. Must be 10 digits";
      }
    }

    if (field === "organizationName") {
      if (!organizationName.trim()) {
        fieldErrors.organizationName = "Please enter an organization name";
      } else if (!validateNameWithNum(organizationName)) {
        fieldErrors.organizationName = "Invalid organization name";
      }
    }

    if (field === "organizationEmail") {
      if (!organizationEmail.trim()) {
        fieldErrors.organizationEmail = "Please enter an email";
      } else if (!validateEmail(organizationEmail)) {
        fieldErrors.organizationEmail = "Invalid email format";
      }
    }

    if (field === "organizationType") {
      fieldErrors.organizationType = organizationType.trim()
        ? ""
        : "Please enter an organization type";
    }

    if (field === "location") {
      fieldErrors.location = location.trim() ? "" : "Please enter a location";
    }

    if (field === "subscriptionPlan") {
      fieldErrors.subscriptionPlan = subscriptionPlan.trim()
        ? ""
        : "Please select a subscription plan";
    }

    // Update validation errors for the specific field
    setValidationErrors((prevErrors) => ({ ...prevErrors, ...fieldErrors }));
  };

  const handleSelectData = (event) => {
    setSubscriptionPlan(event.target.value);

    if (event.target.value) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        subscriptionPlan: "",
      }));
    }
  };

  const goToTenantList = () => {
    navigate(RouteConstants.TENANT_LIST);
  };

  const transformedPlans = subscriptionPlansList.map((plan) => ({
    id: plan._id, // Keep the id for backend submission
    displayName: `${plan.name} (${plan.billingCycle})`, // Create a formatted name
  }));

  const goToLogin = () => {
    removeCookie("x-access-token");
    navigate(RouteConstants.LOGIN);
  };

  const providerValue = {
    name,
    email,
    phone,
    setName,
    setEmail,
    setPhone,
    organizationName,
    setOrganizationName,
    organizationEmail,
    setOrganizationEmail,
    organizationType,
    setOrganizationType,
    location,
    setLocation,
    subscriptionPlan,
    createTenant,
    validationErrors,
    submitSuccessPopup,
    setSubmitSuccessPopup,
    handleBlur,
    handleSelectData,
    transformedPlans,
    spinnerStatus,
    addErrorPopup,
    setAddErrorPopup,
    errorAlertMessage,
    setErrorAlertMessage,
    errorPopup,
    setErrorPopup,
    getSubscriptionPlans,
    goToTenantList,

    errorPopupMessage,
    setErrorPopupMessage,
    buttonText,
    setButtonText,
    goToLogin,
    statusValue,
    setValidationErrors,
    validateName,
    validateEmail,
    validateMobileNumber,
    validateNameWithNum,
  };

  return (
    <AddTenantContext.Provider value={providerValue}>
      {children}
    </AddTenantContext.Provider>
  );
};
